import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Modal from "react-modal";
import { getImageUrl } from "../../../commons/assets_path";
import styles from "./SettingModal.module.scss";
import SettingContent from "./SettingContent";
import SettingFullContent from "./SettingFullContent";

import { getFullPageFlg } from "../../../reducks/store/Settings/selectors";
import { setFullPageFlg, setCurrentPage } from "../../../reducks/store/Settings/slices";

import type { RootState, AppDispatch } from "../../../reducks/store/Settings/store";
import { useTranslation } from "react-i18next";

type Props = {
  modalIsOpen: boolean;
  closeModal: () => void;
  client: {
    code: string;
    id: number;
    plan_code: string;
    country_code: string;
  };
  isPartner: boolean;
  client_member_id: number;
  memberId: number;
  service_icon: {
    slack: string;
    chatwork: string;
  };
  myAccount: {
    email: string;
    name: string;
    id: number;
    role: string;
  };
  updateTaskLimitPolicy: boolean;
  isAdmin: boolean;
  new_member_session_path: string;
  workspaces_path: string;
  past_trial_plans: [];
  current_trial_plan: string;
  plans: object;
  plan_prices: object;
  members_count: number;
  available_trial_plans: [];
  client_member_members_policy: boolean;
  stripe_billing_edit_policy: boolean;
  subscription_show_policy: boolean;
  secure_ip_retrieve_policy: boolean;
  login_setting_retrieve_policy: boolean;
  workspaceLinkPolicy: boolean;
  is_internal: boolean;
  destroy_member_session_path: string;
  locale: string;
  cardStatusNotice: string;
  cardStatus: string;
};

const SettingModal = ({
  modalIsOpen,
  closeModal,
  client,
  isPartner,
  client_member_id,
  service_icon,
  myAccount,
  updateTaskLimitPolicy,
  isAdmin,
  new_member_session_path,
  workspaces_path,
  past_trial_plans,
  current_trial_plan,
  plans,
  plan_prices,
  members_count,
  available_trial_plans,
  memberId,
  client_member_members_policy,
  stripe_billing_edit_policy,
  subscription_show_policy,
  secure_ip_retrieve_policy,
  login_setting_retrieve_policy,
  workspaceLinkPolicy,
  is_internal,
  destroy_member_session_path,
  locale,
  cardStatusNotice,
  cardStatus,
}: Props) => {
  const { t } = useTranslation();
  const [modalCss, setModalCss] = useState<{ width: string; height: string }>({ width: "100%", height: "auto" });
  const [headerShow, setHeaderShow] = useState<boolean>(true);
  const dispatch = useDispatch<AppDispatch>();
  const selector = useSelector((state: RootState) => state);
  const fullPageFlg = getFullPageFlg(selector);

  const customStyles = {
    overlay: {
      zIndex: 1500,
      background: "rgba(34,34,34, 0.3)",
    },
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      maxWidth: 1167,
      width: modalCss.width,
      height: modalCss.height,
      borderRadius: 12,
      padding: 0,
      maxHeight: "90vh",
    },
  };

  const getParent = (): HTMLElement => {
    const parent = document.getElementById("yoom-container");
    if (!parent) {
      throw new Error("Parent element not found");
    }
    return parent;
  };

  const changeModalCss = (width: string, height: string) => {
    setModalCss({ width, height });
  };

  const returnPage = () => dispatch(setFullPageFlg(false));

  return (
    <Modal
      isOpen={modalIsOpen}
      onRequestClose={() => {
        dispatch(setFullPageFlg(false));
        dispatch(setCurrentPage("account"));
        closeModal();
      }}
      style={customStyles}
      contentLabel="Example Modal"
      parentSelector={getParent}
      bodyOpenClassName="ReactModal__Body--open"
    >
      {cardStatusNotice && (
        <div
          className={
            cardStatus === "plan_upgrade_fail" || cardStatus === "update_fail"
              ? "flash_message_for_alert_message alert"
              : "flash_message notice"
          }
          style={{ zIndex: 9999 }}
        >
          {cardStatusNotice}
        </div>
      )}
      {/* http://localhost/test/sections/3?category=plan-and-billing&status=fail */}
      {headerShow && (
        <div>
          <div className={`${styles.modalHeader}`}>
            {fullPageFlg ? (
              <span className={`${styles.modalHeaderReturnTitle}`} onClick={returnPage}>
                &lt;&nbsp;{t("SettingModal.戻る")}
              </span>
            ) : (
              <span className={`${styles.modalHeaderTitle}`}>{t("SettingModal.設定")}</span>
            )}
            <span
              className={`${styles.modalHeaderClose}`}
              onClick={() => {
                dispatch(setFullPageFlg(false));
                dispatch(setCurrentPage("account"));
                closeModal();
              }}
            >
              <img src={getImageUrl("images/icons/close.svg")} alt="" width={24} height={24} />
            </span>
          </div>
        </div>
      )}
      {fullPageFlg ? (
        <SettingFullContent
          client={client}
          plans={plans}
          plan_prices={plan_prices}
          current_trial_plan={current_trial_plan}
          past_trial_plans={past_trial_plans}
          member_count={members_count}
          available_trial_plans={available_trial_plans}
          changeModalCss={changeModalCss}
          setHeaderShow={setHeaderShow}
          is_internal={is_internal}
          locale={locale}
          cardStatus={cardStatus}
        />
      ) : (
        <SettingContent
          client={client}
          isPartner={isPartner}
          client_member_id={client_member_id}
          memberId={memberId}
          service_icon={service_icon}
          updateTaskLimitPolicy={updateTaskLimitPolicy}
          isAdmin={isAdmin}
          new_member_session_path={new_member_session_path}
          members_count={members_count}
          myAccount={myAccount}
          client_member_members_policy={client_member_members_policy}
          stripe_billing_edit_policy={stripe_billing_edit_policy}
          subscription_show_policy={subscription_show_policy}
          secure_ip_retrieve_policy={secure_ip_retrieve_policy}
          login_setting_retrieve_policy={login_setting_retrieve_policy}
          workspaces_path={workspaces_path}
          workspaceLinkPolicy={workspaceLinkPolicy}
          is_internal={is_internal}
          destroy_member_session_path={destroy_member_session_path}
          locale={locale}
          changeModalCss={changeModalCss}
          setHeaderShow={setHeaderShow}
        />
      )}
    </Modal>
  );
};

export default SettingModal;
